import { BALANCE, type Cell, type EntityId, type Transition } from '@2k48/shared';
import { NodePool } from './NodePool';
import { dur, gsap } from './motion';
import { metrics } from './debug/metrics';

/**
 * The board renderer.
 *
 * Everything in here exists to satisfy one requirement: 60fps on a mid-range
 * phone. The rules that follow from that are not style preferences, they are
 * the design:
 *
 *  1. **Persistent nodes.** One DOM node per entity id, created once and
 *     reused for that entity's whole life. The board is never rebuilt from
 *     `innerHTML`. Rebuilding means the browser re-parses, re-styles and
 *     re-lays-out the whole grid on every move, and it also throws away the
 *     nodes GSAP is mid-tween on.
 *
 *  2. **Transform and opacity only.** Positions are `translate3d`, emphasis is
 *     `scale`, glow is `opacity` on a pre-painted layer. Nothing here writes
 *     `top`, `left`, `width`, `height` or `margin` on an animating node —
 *     those force layout, and one forced layout eats the frame budget.
 *
 *  3. **Diffing.** `sync()` compares against what is already on screen and
 *     touches only what changed.
 *
 *  4. **Layout is read at most once per resize**, never per frame. All
 *     positions are arithmetic on cached numbers.
 */

export interface EntitySnapshot {
  id: EntityId;
  cell: Cell;
  value?: number;
  /** Visual variant. Must be one of the tones in game.css. */
  tone?: string;
}

interface EntityView {
  node: HTMLElement;
  face: HTMLElement;
  glow: HTMLElement;
  cell: Cell;
  value: number | undefined;
  tone: string;
}

export interface BoardRendererOptions {
  cols: number;
  rows: number;
  defaultTone?: string;
  /** Called when a transition batch's animation finishes. */
  onSettled?: () => void;
}

const TONES = new Set(['p1', 'p2', 'accent', 'positive', 'info', 'danger', 'special']);

export class BoardRenderer {
  readonly root: HTMLElement;
  private readonly cellsLayer: HTMLElement;
  private readonly entityLayer: HTMLElement;
  private readonly pool: NodePool<HTMLElement>;

  private readonly views = new Map<EntityId, EntityView>();

  private cols: number;
  private rows: number;
  private defaultTone: string;
  private onSettled: (() => void) | undefined;

  /** Cached geometry. Recomputed only on resize. */
  private cellSize = 0;
  private step = 0;

  private running: gsap.core.Timeline | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(container: HTMLElement, options: BoardRendererOptions) {
    this.cols = options.cols;
    this.rows = options.rows;
    this.defaultTone = options.defaultTone ?? 'p1';
    this.onSettled = options.onSettled;

    this.root = document.createElement('div');
    this.root.className = 'board';

    this.cellsLayer = document.createElement('div');
    this.cellsLayer.className = 'board__cells';

    this.entityLayer = document.createElement('div');
    this.entityLayer.className = 'board__entities';

    this.root.append(this.cellsLayer, this.entityLayer);
    container.appendChild(this.root);

    this.pool = new NodePool(
      () => createEntityNode(),
      this.entityLayer,
      BALANCE.perf.entityPoolSize,
      'entities',
    );

    this.setGrid(this.cols, this.rows);

    // One layout read per resize, and never during a frame that is animating.
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.root);
  }

  // --- grid ------------------------------------------------------------------

  /**
   * Build the static background cells. They are painted once and never enter a
   * diff — they cannot change without the board itself changing shape.
   */
  setGrid(cols: number, rows: number): void {
    this.cols = cols;
    this.rows = rows;
    this.root.style.setProperty('--cols', String(cols));
    this.root.style.setProperty('--rows', String(rows));

    this.cellsLayer.replaceChildren();
    const fragment = document.createDocumentFragment();
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const cell = document.createElement('div');
        cell.className = 'cell';
        cell.dataset.col = String(col);
        cell.dataset.row = String(row);
        fragment.appendChild(cell);
      }
    }
    this.cellsLayer.appendChild(fragment);

    this.measure();
  }

  /**
   * The only place in the render path that reads layout.
   *
   * `--cell` is computed by CSS from the design unit, so rather than parsing a
   * custom property (whose computed form is not reliably resolved) we measure
   * one real cell and derive the gap from the layer width. Both reads happen
   * on resize, never inside an animation.
   */
  private measure(): void {
    const sample = this.cellsLayer.firstElementChild as HTMLElement | null;
    if (!sample) return;

    const cellRect = sample.getBoundingClientRect();
    const layerRect = this.cellsLayer.getBoundingClientRect();
    if (cellRect.width === 0 || layerRect.width === 0) return;

    this.cellSize = cellRect.width;
    this.step = this.cols > 1 ? (layerRect.width - this.cellSize) / (this.cols - 1) : this.cellSize;

    // Static cells: positioned by transform like everything else, so the board
    // has exactly one positioning model.
    const cells = this.cellsLayer.children;
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i] as HTMLElement;
      const col = Number(cell.dataset.col);
      const row = Number(cell.dataset.row);
      cell.style.transform = `translate3d(${col * this.step}px, ${row * this.step}px, 0)`;
    }

    // Existing entities keep their logical cell and get re-placed instantly.
    for (const view of this.views.values()) {
      gsap.set(view.node, { x: this.x(view.cell), y: this.y(view.cell) });
    }
  }

  /**
   * Board-space pixels for a cell, from the geometry cached at the last resize.
   *
   * Public because the effects layer needs exactly the answer the entities got.
   * Re-deriving it from the DOM there meant a layout read per effect, and
   * effects arrive in bursts — a cascade of merges would flush layout once per
   * sparkle, in the frame least able to afford it.
   */
  positionOf(cell: Cell): { x: number; y: number } {
    return { x: this.x(cell), y: this.y(cell) };
  }

  private x(cell: Cell): number {
    return cell.col * this.step;
  }

  private y(cell: Cell): number {
    return cell.row * this.step;
  }

  // --- entities --------------------------------------------------------------

  private viewFor(id: EntityId): EntityView | undefined {
    return this.views.get(id);
  }

  private createView(snapshot: EntitySnapshot): EntityView {
    const node = this.pool.acquire();
    const face = node.querySelector<HTMLElement>('.entity__face');
    const glow = node.querySelector<HTMLElement>('.entity__glow');
    if (!face || !glow) throw new Error('Pooled entity node is missing its layers.');

    const view: EntityView = {
      node,
      face,
      glow,
      cell: snapshot.cell,
      value: snapshot.value,
      tone: this.toneOf(snapshot),
    };

    node.hidden = false;
    node.dataset.entityId = snapshot.id;
    node.dataset.tone = view.tone;
    if (snapshot.value !== undefined) node.dataset.value = String(snapshot.value);
    face.textContent = snapshot.value === undefined ? '' : String(snapshot.value);

    gsap.set(node, { x: this.x(snapshot.cell), y: this.y(snapshot.cell), scale: 1, opacity: 1 });
    gsap.set(glow, { opacity: 0 });

    this.views.set(snapshot.id, view);
    this.reportCounts();
    return view;
  }

  private toneOf(snapshot: EntitySnapshot): string {
    if (snapshot.tone && TONES.has(snapshot.tone)) return snapshot.tone;
    return this.defaultTone;
  }

  private destroyView(id: EntityId): void {
    const view = this.views.get(id);
    if (!view) return;
    this.views.delete(id);
    view.node.hidden = true;
    delete view.node.dataset.entityId;
    delete view.node.dataset.value;
    this.pool.release(view.node);
    this.reportCounts();
  }

  private reportCounts(): void {
    metrics.setNodeCount(this.root.getElementsByTagName('*').length);
    const stats = this.pool.stats;
    metrics.setPool(stats.free, stats.busy);
  }

  // --- full sync (no animation) ----------------------------------------------

  /**
   * Diff the board against a snapshot and touch only what differs.
   *
   * Used for a hard resync — joining mid-match, reconnecting, or a server
   * correction the client mispredicted. It is not the normal path: normally the
   * board changes through `apply()`, because a snapshot cannot say where
   * anything came from.
   */
  sync(entities: readonly EntitySnapshot[]): void {
    const seen = new Set<EntityId>();

    for (const snapshot of entities) {
      seen.add(snapshot.id);
      const view = this.viewFor(snapshot.id);

      if (!view) {
        this.createView(snapshot);
        continue;
      }

      if (view.cell.col !== snapshot.cell.col || view.cell.row !== snapshot.cell.row) {
        view.cell = snapshot.cell;
        gsap.set(view.node, { x: this.x(snapshot.cell), y: this.y(snapshot.cell) });
      }

      if (view.value !== snapshot.value) {
        view.value = snapshot.value;
        view.face.textContent = snapshot.value === undefined ? '' : String(snapshot.value);
        if (snapshot.value === undefined) delete view.node.dataset.value;
        else view.node.dataset.value = String(snapshot.value);
      }

      const tone = this.toneOf(snapshot);
      if (view.tone !== tone) {
        view.tone = tone;
        view.node.dataset.tone = tone;
      }
    }

    for (const id of [...this.views.keys()]) {
      if (!seen.has(id)) this.destroyView(id);
    }
  }

  // --- transitions -----------------------------------------------------------

  /**
   * Finish whatever is animating, immediately.
   *
   * This is what makes input feel responsive while things are moving: a new
   * input does not wait in a queue and is not dropped. The timeline in flight
   * jumps to its last frame — so the board is in a valid, settled state — and
   * the next one starts from there.
   *
   * The alternatives are both worse. Queueing lets a fast player build a
   * backlog the board plays back seconds later. Dropping makes the game ignore
   * taps, which reads as broken rather than as slow.
   */
  finishRunning(): void {
    if (!this.running) return;
    const running = this.running;
    this.running = null;
    running.progress(1).kill();
  }

  get isAnimating(): boolean {
    return this.running !== null && this.running.isActive();
  }

  /**
   * Animate a batch of transitions.
   *
   * `stage` groups them: everything in stage 0 plays together, then stage 1,
   * then stage 2. That is what lets a ruleset say "slide, then merge, then
   * spawn" without knowing GSAP exists.
   */
  apply(transitions: readonly Transition[]): gsap.core.Timeline {
    this.finishRunning();

    const timeline = gsap.timeline({
      onComplete: () => {
        this.running = null;
        this.onSettled?.();
      },
    });

    if (transitions.length === 0) {
      this.running = null;
      this.onSettled?.();
      return timeline;
    }

    const stages = new Map<number, Transition[]>();
    for (const transition of transitions) {
      const stage = transition.stage ?? 0;
      const bucket = stages.get(stage);
      if (bucket) bucket.push(transition);
      else stages.set(stage, [transition]);
    }

    const ordered = [...stages.keys()].sort((a, b) => a - b);
    const removals: EntityId[] = [];

    for (const [index, stage] of ordered.entries()) {
      const label = `stage-${stage}`;
      timeline.addLabel(label, index === 0 ? 0 : '>');
      const batch = stages.get(stage) as Transition[];
      this.buildStage(timeline, batch, label, removals);
    }

    timeline.call(() => {
      for (const id of removals) this.destroyView(id);
    });

    this.running = timeline;
    return timeline;
  }

  private buildStage(
    timeline: gsap.core.Timeline,
    batch: readonly Transition[],
    position: string,
    removals: EntityId[],
  ): void {
    const { motion } = BALANCE;
    const moveDuration = dur(motion.moveSec);
    const mergeHalf = dur(motion.mergeSec) / 2;
    const mergeAt = `${position}+=${moveDuration}`;

    for (const transition of batch) {
      switch (transition.kind) {
        case 'spawn': {
          const view =
            this.viewFor(transition.id) ??
            this.createView({
              id: transition.id,
              cell: transition.at,
              ...(transition.value !== undefined ? { value: transition.value } : {}),
              ...(toneFromTags(transition.tags) ? { tone: toneFromTags(transition.tags) } : {}),
            });
          gsap.set(view.node, { scale: 0, opacity: 0 });
          timeline.to(
            view.node,
            {
              scale: 1,
              opacity: 1,
              duration: dur(motion.spawnSec),
              ease: motion.ease.pop,
            },
            position,
          );
          break;
        }

        case 'move': {
          const view = this.viewFor(transition.id);
          if (!view) break;
          view.cell = transition.to;
          timeline.to(
            view.node,
            {
              x: this.x(transition.to),
              y: this.y(transition.to),
              duration: dur(motion.moveSec),
              ease: motion.ease.move,
            },
            position,
          );
          break;
        }

        case 'merge': {
          const view = this.viewFor(transition.id);
          const survivor = this.viewFor(transition.into);
          if (view) {
            view.cell = transition.to;
            timeline.to(
              view.node,
              {
                x: this.x(transition.to),
                y: this.y(transition.to),
                duration: moveDuration,
                ease: motion.ease.move,
              },
              position,
            );
            timeline.to(
              view.node,
              {
                scale: 0,
                opacity: 0,
                duration: mergeHalf,
                ease: motion.ease.fade,
              },
              mergeAt,
            );
            // Recycled after the whole timeline, not mid-flight: releasing a
            // node GSAP is still writing to is how a "ghost" tile happens.
            removals.push(transition.id);
          }
          if (survivor) {
            if (transition.value !== undefined) {
              timeline.call(
                () => {
                  survivor.value = transition.value;
                  survivor.face.textContent = String(transition.value);
                  survivor.node.dataset.value = String(transition.value);
                },
                undefined,
                mergeAt,
              );
            }
            // Every survivor pops at the same absolute point after all slides.
            timeline.to(
              survivor.node,
              {
                scale: motion.mergePopScale,
                duration: mergeHalf,
                ease: motion.ease.pop,
              },
              mergeAt,
            );
            timeline.to(
              survivor.node,
              { scale: 1, duration: mergeHalf },
              `${mergeAt}+=${mergeHalf}`,
            );
            timeline.to(survivor.glow, { opacity: 1, duration: mergeHalf }, mergeAt);
            timeline.to(
              survivor.glow,
              { opacity: 0, duration: mergeHalf },
              `${mergeAt}+=${mergeHalf}`,
            );
          }
          break;
        }

        case 'update': {
          const view = this.viewFor(transition.id);
          if (!view) break;
          const tone = toneFromTags(transition.tags);
          timeline.call(
            () => {
              if (transition.value !== undefined) {
                view.value = transition.value;
                view.face.textContent = String(transition.value);
                view.node.dataset.value = String(transition.value);
              }
              if (tone) {
                view.tone = tone;
                view.node.dataset.tone = tone;
              }
            },
            undefined,
            position,
          );
          break;
        }

        case 'remove': {
          const view = this.viewFor(transition.id);
          if (!view) break;
          timeline.to(
            view.node,
            {
              scale: 0,
              opacity: 0,
              duration: dur(motion.removeSec),
              ease: motion.ease.fade,
            },
            position,
          );
          removals.push(transition.id);
          break;
        }

        default: {
          // `x:` transitions are ruleset-defined effects. The board does not
          // know what they mean; the effects layer does. Reserving the slot
          // here keeps the switch exhaustive and documents where they go.
          break;
        }
      }
    }
  }

  // --- teardown --------------------------------------------------------------

  destroy(): void {
    this.finishRunning();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.views.clear();
    this.pool.destroy();
    this.root.remove();
  }
}

function toneFromTags(tags: readonly string[] | undefined): string | undefined {
  return tags?.find((tag) => TONES.has(tag));
}

/**
 * The entity node, built once per pool slot.
 *
 * Three nested elements rather than one: the outer node owns the transform,
 * the face owns the colour and the hard shadow, and the glow owns a
 * pre-rasterised blur that is only ever revealed with opacity. Collapsing them
 * would mean animating a box-shadow, which repaints every frame.
 */
function createEntityNode(): HTMLElement {
  const node = document.createElement('div');
  node.className = 'entity';
  node.hidden = true;

  const glow = document.createElement('div');
  glow.className = 'entity__glow';

  const face = document.createElement('div');
  face.className = 'entity__face';

  node.append(glow, face);
  return node;
}
